<script setup lang="ts">
/**
 * Nominee Resident Director Agreement — SG Incorporation (Post)
 * Word 템플릿 핵심 조항 + Schedule 1/2 포함.
 */
import { ref } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';

let apSeq = 1, prinSeq = 1, ndirSeq = 1, nshSeq = 1, authSeq = 1;
const apRows = ref<number[]>([1]);
const prinRows = ref<number[]>([1]);
const ndirRows = ref<number[]>([1]);
const nshRows = ref<number[]>([1]);
const authRows = ref<number[]>([1]);

function addAp() { apRows.value.push(++apSeq); }
function delAp(id: number) {
  if (apRows.value.length <= 1) return;
  apRows.value = apRows.value.filter((x) => x !== id);
}
function addPrin() { prinRows.value.push(++prinSeq); }
function delPrin(id: number) {
  if (prinRows.value.length <= 1) return;
  prinRows.value = prinRows.value.filter((x) => x !== id);
}
function addNdir() { ndirRows.value.push(++ndirSeq); }
function delNdir(id: number) {
  if (ndirRows.value.length <= 1) return;
  ndirRows.value = ndirRows.value.filter((x) => x !== id);
}
function addNsh() { nshRows.value.push(++nshSeq); }
function delNsh(id: number) {
  if (nshRows.value.length <= 1) return;
  nshRows.value = nshRows.value.filter((x) => x !== id);
}
function addAuth() { authRows.value.push(++authSeq); }
function delAuth(id: number) {
  if (authRows.value.length <= 1) return;
  authRows.value = authRows.value.filter((x) => x !== id);
}
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:15px">NOMINEE RESIDENT DIRECTOR AGREEMENT</div>
    </div>

    <p class="doc-p">This AGREEMENT is made this <CddVarField field-key="agreement_date" label="Agreement Date" />.</p>
    <p class="doc-p"><b>BETWEEN:</b></p>
    <p class="doc-p">
      (1) <CddVarField field-key="company_name" label="Company Name" />
      (Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." />),
      a company incorporated in the Republic of Singapore having its registered address at
      <CddVarField field-key="company_registered_address" label="Company Registered Address" /> ("Company");
    </p>
    <p class="doc-p">
      (2) <CddVarField field-key="principal_name" label="Principal Name" />
      (Passport / Identification No. <CddVarField field-key="principal_identification_no" label="Principal ID" />)
      of <CddVarField field-key="principal_address" label="Principal Address" /> ("Principal"); and
    </p>
    <p class="doc-p">
      (3) <CddVarField field-key="csp_name" label="CSP Name" />
      (Company Registration No. <CddVarField field-key="csp_uen" label="CSP UEN" />),
      a company incorporated in the Republic of Singapore having its registered address at
      <CddVarField field-key="csp_full_address" label="CSP Full Address" /> (the "Service Provider").
    </p>

    <p class="doc-p"><b>WHEREAS:</b> The Principal and the Company wish to appoint the Service Provider to provide certain services under the terms and conditions hereto.</p>
    <p class="doc-p"><b>IT IS AGREED AS FOLLOWS:</b></p>

    <p class="doc-sec-title" style="text-align:left">1. NATURE OF SERVICES PROVIDED</p>
    <p class="doc-p">
      1.1 The Company has requested that the Service Provider provide the services of certain individual(s),
      each of whom is hereby appointed to act in the capacity of a nominee resident Director of the Company
      (hereinafter referred to as "Nominee Director") upon the terms and conditions hereto ("Services"). The
      consideration for the Services is set out in Clause 3 of this Agreement.
    </p>
    <p class="doc-p">
      1.2 Subject to the other terms of this Agreement, the Nominee Director is requested to act upon the
      instructions of the Principal and/or Authorised Person(s) set out under Schedule 1. If more than one
      person, the Service Provider and/or the Nominee Director may act upon the instructions of any of them.
    </p>

    <p class="doc-sec-title" style="text-align:left">2. SCOPE OF APPOINTMENT</p>
    <p class="doc-p">
      2.1 The Service Provider shall procure the appointment of one or more individuals that it deems fit to
      perform the functions of the Nominee Director, generally to act or refrain from acting in respect of the
      Company upon the lawful instructions of the Principal and other Authorised Persons, including the signing
      of routine notices, minutes, returns, and other routine documents in compliance with the Companies Act
      1967 or any other statutory provisions.
    </p>
    <p class="doc-p">
      2.2 The Nominee Director shall not be deemed or required to be involved in any day-to-day management
      functions, operations or decision-making. The Nominee Director shall not give or be required to give any
      personal guarantees as security for any indebtedness of the Company.
    </p>
    <p class="doc-p">
      2.3 Subject to Clause 4, the Nominee Director shall sign a directors' resolution upon written
      instructions of the Principal and/or Authorised Person(s) after such resolution has been approved and
      pre-signed by all other directors (or, if the Board consists of only one Nominee Director, upon written
      instructions alone). Liability for the content and implications of any directors' resolution remains with
      the Company, Principal and/or Authorised Person(s).
    </p>
    <p class="doc-p">
      2.4–2.7 Replacement of Nominee Director, required resolutions/documents to give effect to appointment or
      change, and Service Provider's Affiliates' right of access to Company documents — as set out in the
      standard Nominee Resident Director Agreement.
    </p>

    <p class="doc-sec-title" style="text-align:left">3. FEES AND EXPENSES</p>
    <p class="doc-p">
      3.1 Annual Fee:
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="annual_fee_nominee" label="Annual Fee (Nominee)" />/-
      payable upon signing for the first 12-month period, thereafter in advance for each subsequent cycle.
      Disbursements invoiced monthly, payable within
      <CddVarField field-key="invoice_payment_days" label="Invoice Payment Days" /> days.
    </p>
    <p class="doc-p">
      3.2 Security Deposit:
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="deposit_amount" label="Deposit Amount" />/-
      refundable (without interest) within
      <CddVarField field-key="deposit_refund_days" label="Deposit Refund Days" />
      days after termination upon satisfactory resignation of the Nominee Director and discharge of residual
      obligations.
    </p>
    <p class="doc-p">
      3.3 The Service Provider may apply the Deposit to set-off liabilities or costs of liquidation / striking off
      in the event of default.
    </p>
    <p class="doc-p">
      3.4 Late interest charges of
      <CddVarField field-key="late_interest_rate" label="Late Interest Rate %" />% p.a. shall be payable on
      outstanding Annual Fees and/or invoices.
    </p>

    <p class="doc-sec-title" style="text-align:left">4. WRITTEN INSTRUCTIONS</p>
    <p class="doc-p">
      4.1 The Principal and/or Authorised Person(s) shall give written instructions in the manner set out under
      Clause 7. The Service Provider and/or Nominee Director may refuse instructions that are illegal,
      unlawful, fraudulent and/or in breach of duties.
    </p>
    <p class="doc-p">
      4.2 Changes to name, identity number, address and specimen signature of the Principal or Authorised
      Person(s) must be notified in the same manner.
    </p>
    <p class="doc-p">
      4.3 Substantive response to any request for instructions shall be given within twenty-one (21) days.
    </p>

    <p class="doc-sec-title" style="text-align:left">5. INDEMNITY, WARRANTIES AND OTHER UNDERTAKINGS</p>
    <p class="doc-p">
      5.1 The Company and Principal jointly and severally undertake to indemnify the Service Provider and
      Affiliates (including the Nominee Director); maintain D&amp;O / PI cover; use the Service Provider's
      registered address; ensure legality and ethics of all acts; furnish updated legal/beneficial ownership;
      maintain compliance, accounts and tax filings; keep the Service Provider informed of matters affecting
      the Company; not engage in illegal activities; ensure solvency; and give at least
      <CddVarField field-key="cessation_notice_months" label="Cessation Notice (Months)" />
      month(s) prior written notice if intending to cease business — as more particularly set out in the
      standard agreement clauses 5.1(a)–(p).
    </p>

    <p class="doc-sec-title" style="text-align:left">6. JOINT AND SEVERAL LIABILITY</p>
    <p class="doc-p">
      6.1 The Principal unequivocally guarantees the due performance of all the Company's obligations. The
      obligations of the Company and the Principal are joint and several.
    </p>

    <p class="doc-sec-title" style="text-align:left">7. NOTICES</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Company</td>
          <td>
            <CddVarField field-key="company_name" label="Company Name" /><br />
            <CddVarField field-key="company_registered_address" label="Company Registered Address" />
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Principal</td>
          <td>
            <CddVarField field-key="principal_name" label="Principal Name" /><br />
            <CddVarField field-key="principal_address" label="Principal Address" />
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Service Provider</td>
          <td>
            <CddVarField field-key="csp_name" label="CSP Name" /><br />
            <CddVarField field-key="csp_full_address" label="CSP Full Address" />
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p" style="margin-top:8px">
      Notices may be delivered personally, by prepaid registered/certified post, facsimile or email, with
      deemed service as set out in the standard agreement.
    </p>

    <p class="doc-sec-title" style="text-align:left">8. DEFAULT AND TERMINATION</p>
    <p class="doc-p">
      8.1 Default events include breach, untrue representations, undischarged litigation/insolvency
      proceedings, criminal/regulatory proceedings, Part IX declared company status, or fraudulent/illegal
      activities (including ML/TF).
    </p>
    <p class="doc-p">
      8.2 On default the Service Provider may suspend Services, forfeit the Deposit, terminate immediately,
      and/or take protective action (including striking off).
    </p>
    <p class="doc-p">
      8.3 Either party may terminate by giving
      <CddVarField field-key="termination_notice_months" label="Termination Notice (Months)" />
      month(s) written notice or payment of pro-rated Annual Fee in lieu.
    </p>
    <p class="doc-p">
      8.4–8.6 Upon termination: settle dues; appoint replacement resident director within five (5) working days;
      execute resignation/appointment documents. Service Provider may freeze/close bank accounts and strike off
      the Company. Each Principal/Authorised Person who is a director consents to striking off under Clause 8.6.
    </p>

    <p class="doc-sec-title" style="text-align:left">9. MISCELLANEOUS</p>
    <p class="doc-p">
      Entire agreement; severability; assignment; enduring indemnity; limitation of liability (except gross
      negligence / wilful misconduct); Contracts (Rights of Third Parties) Act 2001 with Affiliates enforcement rights.
    </p>

    <p class="doc-sec-title" style="text-align:left">10. GOVERNING LAW &amp; JURISDICTION</p>
    <p class="doc-p">
      Laws of the Republic of Singapore; submission to the Courts in Singapore.
    </p>

    <p class="doc-lbl-title">IN WITNESS WHEREOF</p>
    <p class="doc-p"><b>The Company</b> — SIGNED by its authorised signatory for and on behalf of
      <CddVarField field-key="company_name" label="Company Name" /> in the presence of:
    </p>
    <p class="doc-p">
      Name: <CddVarField field-key="company_signatory_name" label="Company Signatory" /><br />
      Designation: <CddVarField field-key="company_signatory_title" label="Company Title" /><br />
      Witness Name: <CddVarField field-key="company_witness_name" label="Company Witness" /><br />
      ID / Passport No.: <CddVarField field-key="company_witness_identification_no" label="Company Witness ID" />
    </p>

    <p class="doc-p"><b>The Principal</b> — SIGNED by <CddVarField field-key="principal_name" label="Principal Name" /> in the presence of:</p>
    <p class="doc-p">
      Witness Name: <CddVarField field-key="principal_witness_name" label="Principal Witness" /><br />
      ID / Passport No.: <CddVarField field-key="principal_witness_identification_no" label="Principal Witness ID" />
    </p>

    <p class="doc-p"><b>The Service Provider</b> — SIGNED for and on behalf of
      <CddVarField field-key="csp_name" label="CSP Name" /> in the presence of:
    </p>
    <p class="doc-p">
      Name: <CddVarField field-key="csp_signatory_name" label="CSP Signatory" /><br />
      Designation: <CddVarField field-key="csp_signatory_title" label="CSP Title" /><br />
      Witness Name: <CddVarField field-key="csp_witness_name" label="CSP Witness" /><br />
      ID / Passport No.: <CddVarField field-key="csp_witness_identification_no" label="CSP Witness ID" />
    </p>

    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.nominee" board-label="NOMINEE DIRECTOR & PRINCIPAL" />

    <p class="doc-lbl-title">Authorised Person(s) — Clause 8.6 consent</p>
    <div v-for="(id, i) in apRows" :key="'ap_'+id" class="sched-block">
      <p class="doc-p">
        SIGNED by <CddVarField :field-key="`ap_name_${id}`" :label="`Authorised Person ${i + 1}`" /> in the presence of:<br />
        Witness Name: <CddVarField :field-key="`ap_witness_name_${id}`" :label="`AP Witness ${i + 1}`" /><br />
        ID / Passport No.: <CddVarField :field-key="`ap_witness_id_${id}`" :label="`AP Witness ID ${i + 1}`" />
        <button type="button" class="cdd-rowbtn" :disabled="apRows.length <= 1" @click="delAp(id)">×</button>
      </p>
    </div>
    <button type="button" class="cdd-addrow" @click="addAp">+ Add authorised person</button>

    <div class="page-div">— Schedule 1 —</div>
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center">SCHEDULE 1</div>
      <div class="doc-meta">PARTICULARS &amp; SPECIMEN SIGNATURES OF PRINCIPAL(S) AND AUTHORISED PERSON(S)</div>
    </div>

    <p class="doc-lbl-title">Name of Principal(s)</p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Name</th><th>Passport / ID No.</th><th>Address</th><th>Nominator</th><th style="width:36px" /></tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in prinRows" :key="'p_'+id">
          <td><CddVarField :field-key="`prin_name_${id}`" :label="`Principal ${i + 1} Name`" /></td>
          <td><CddVarField :field-key="`prin_id_${id}`" :label="`Principal ${i + 1} ID`" /></td>
          <td><CddVarField :field-key="`prin_address_${id}`" :label="`Principal ${i + 1} Address`" /></td>
          <td><CddVarField :field-key="`prin_nominator_${id}`" :label="`Yes/No`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="prinRows.length <= 1" @click="delPrin(id)">×</button></td>
        </tr>
        <tr><td colspan="5"><button type="button" class="cdd-addrow" @click="addPrin">+ Add principal</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">Name of Director(s)</p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Name</th><th>Passport / ID No.</th><th>Address</th><th>Nominator</th><th style="width:36px" /></tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in ndirRows" :key="'d_'+id">
          <td><CddVarField :field-key="`ndir_name_${id}`" :label="`Director ${i + 1} Name`" /></td>
          <td><CddVarField :field-key="`ndir_id_${id}`" :label="`Director ${i + 1} ID`" /></td>
          <td><CddVarField :field-key="`ndir_address_${id}`" :label="`Director ${i + 1} Address`" /></td>
          <td><CddVarField :field-key="`ndir_nominator_${id}`" :label="`Yes/No`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="ndirRows.length <= 1" @click="delNdir(id)">×</button></td>
        </tr>
        <tr><td colspan="5"><button type="button" class="cdd-addrow" @click="addNdir">+ Add director</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">Name of Shareholder(s)</p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Name</th><th>Passport / ID No.</th><th>Address</th><th>Nominator</th><th style="width:36px" /></tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in nshRows" :key="'s_'+id">
          <td><CddVarField :field-key="`nsh_name_${id}`" :label="`Shareholder ${i + 1} Name`" /></td>
          <td><CddVarField :field-key="`nsh_id_${id}`" :label="`Shareholder ${i + 1} ID`" /></td>
          <td><CddVarField :field-key="`nsh_address_${id}`" :label="`Shareholder ${i + 1} Address`" /></td>
          <td><CddVarField :field-key="`nsh_nominator_${id}`" :label="`Yes/No`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="nshRows.length <= 1" @click="delNsh(id)">×</button></td>
        </tr>
        <tr><td colspan="5"><button type="button" class="cdd-addrow" @click="addNsh">+ Add shareholder</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">Name of Authorised Person(s)</p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Name</th><th>Passport / ID No.</th><th>Address</th><th>Nominator</th><th style="width:36px" /></tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in authRows" :key="'at_'+id">
          <td><CddVarField :field-key="`auth_name_${id}`" :label="`Auth ${i + 1} Name`" /></td>
          <td><CddVarField :field-key="`auth_id_${id}`" :label="`Auth ${i + 1} ID`" /></td>
          <td><CddVarField :field-key="`auth_address_${id}`" :label="`Auth ${i + 1} Address`" /></td>
          <td><CddVarField :field-key="`auth_nominator_${id}`" :label="`Yes/No`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="authRows.length <= 1" @click="delAuth(id)">×</button></td>
        </tr>
        <tr><td colspan="5"><button type="button" class="cdd-addrow" @click="addAuth">+ Add authorised person</button></td></tr>
      </tbody>
    </table>

    <div class="page-div">— Schedule 2 —</div>
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center">SCHEDULE 2 — SERVICE AGREEMENT</div>
    </div>
    <p class="doc-p">This AGREEMENT is made this <CddVarField field-key="agreement_date" label="Agreement Date" />.</p>
    <p class="doc-p">
      BETWEEN (1) <CddVarField field-key="company_name" label="Company Name" />
      (Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." />)
      and (2) <CddVarField field-key="csp_name" label="CSP Name" />
      (Company Registration No. <CddVarField field-key="csp_uen" label="CSP UEN" />).
    </p>
    <p class="doc-lbl-title">Scope of Services and Fees</p>
    <table class="cdd-tbl">
      <thead><tr><th>Scope of Work</th><th>Estimated Fees*</th></tr></thead>
      <tbody>
        <tr>
          <td>
            Annual Preparation of Compilation Report (unaudited Financial Statement)<br />
            Corporate Income Tax Services: Computation and filing of Estimated Chargeable Income;
            Preparation of Corporate tax forms and filing; Preparation of Form C / C-S for submission
          </td>
          <td>
            For active companies: From
            <CddVarField field-key="currency_symbol" label="Currency" />
            <CddVarField field-key="annual_fee_active_company" label="Active Company Fee" />/- p.a.<br />
            For dormant companies: From
            <CddVarField field-key="currency_symbol" label="Currency" />
            <CddVarField field-key="annual_fee_dormant_company" label="Dormant Company Fee" />/- p.a.
          </td>
        </tr>
        <tr>
          <td>
            Monthly, Quarterly or Annual Book-keeping Services; GST (where applicable) — GST Application,
            Review, Quarterly F5 Submission
          </td>
          <td>Periodic book-keeping and GST service fees: to be agreed after consultation.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p" style="font-size:11px; color:#555">
      * Fees are indicative only and based on complexity of work, volume of transactions, and conduct of
      periodic reporting. Estimated fees exclude GST and disbursements, Financial Audit and GST Audit
      liaising, and correction of GST returns.
    </p>
    <p class="doc-p">
      For <CddVarField field-key="csp_name" label="CSP Name" /> —
      <CddVarField field-key="csp_signatory_name" label="CSP Signatory" /> /
      <CddVarField field-key="csp_signatory_title" label="CSP Title" />
    </p>
    <p class="doc-p">
      We accept — For <CddVarField field-key="company_name" label="Company Name" /> —
      <CddVarField field-key="company_signatory_name" label="Company Signatory" /> /
      <CddVarField field-key="company_signatory_title" label="Company Title" />
    </p>
  </div>
</template>

<style scoped>
.sched-block { margin-bottom: 8px; }
</style>
